<template>
    <div class="profile-menu">
        <button
            type="button"
            class="profile-menu__toggle"
            :aria-expanded="open ? 'true' : 'false'"
            aria-haspopup="true"
            aria-controls="profile-menu-panel"
            v-on:click="$emit('toggle')"
        >
            <span
                class="profile-menu__avatar"
                v-if="isAuthenticated && initials"
                aria-hidden="true"
                >{{ initials }}</span
            >
            <img
                v-else
                src="/img/user-solid.svg"
                class="menu-icon d-block d-xl-none"
                alt=""
            />
            <span
                class="d-none d-xl-block"
                :class="{ 'ms-2': isAuthenticated && initials }"
                >{{ $t("Profil") }}</span
            >
        </button>

        <transition name="profile-pop">
            <div
                class="custom-modal active profile-menu__panel"
                id="profile-menu-panel"
                v-if="open"
            >
                <!-- logged out -->
                <div class="loggedout" v-if="!isAuthenticated">
                    <div class="profile-menu__title">
                        {{ $t("Belépés") }}
                    </div>
                    <form v-on:submit.prevent="doLogin" novalidate>
                        <div class="form-floating mb-2">
                            <input
                                type="email"
                                id="profile-menu-email"
                                class="form-control"
                                placeholder="E-mail"
                                autocomplete="username"
                                ref="email"
                                v-model.trim="form.email"
                                required
                            />
                            <label for="profile-menu-email">{{
                                $t("E-mail")
                            }}</label>
                        </div>
                        <div class="form-floating mb-2">
                            <input
                                type="password"
                                id="profile-menu-password"
                                class="form-control"
                                :placeholder="$t('Jelszó')"
                                autocomplete="current-password"
                                v-model="form.password"
                                required
                            />
                            <label for="profile-menu-password">{{
                                $t("Jelszó")
                            }}</label>
                        </div>
                        <div
                            class="text-danger small mb-2"
                            role="alert"
                            v-if="error"
                        >
                            {{ error }}
                        </div>
                        <button
                            type="submit"
                            class="btn btn-primary w-100"
                            :disabled="loading"
                        >
                            <span
                                v-if="loading"
                                class="spinner-border spinner-border-sm me-2"
                                aria-hidden="true"
                            ></span>
                            {{ $t("Belépés") }}
                        </button>
                        <a
                            href="#"
                            class="small-text d-block my-3"
                            v-b-modal.forgotPassword
                            >{{ $t("Elfelejtettem a jelszavamat.") }}</a
                        >
                    </form>
                    <div class="border-top pt-3">
                        <div class="small-text mb-2">
                            {{ $t("Még nincs fiókod?") }}
                        </div>
                        <router-link
                            :to="{ name: 'Registration' }"
                            class="btn btn-secondary w-100"
                            >{{ $t("Regisztráció") }}</router-link
                        >
                    </div>
                </div>

                <!-- logged in -->
                <div class="loggedin" v-else>
                    <div class="profile-menu__user">
                        <span
                            class="profile-menu__avatar profile-menu__avatar--lg"
                            >{{ initials || "?" }}</span
                        >
                        <div class="profile-menu__user-text">
                            <div class="small-text">{{ $t("Szia,") }}</div>
                            <strong>{{ displayName }}</strong>
                            <div class="small-text text-truncate">
                                {{ currentUser.email }}
                            </div>
                        </div>
                    </div>
                    <nav class="profile-menu__links">
                        <router-link
                            v-for="link in links"
                            v-bind:key="link.label"
                            :to="link.to"
                            class="profile-menu__link"
                            :class="{ active: isActive(link) }"
                        >
                            <b-icon :icon="link.icon" aria-hidden="true" />
                            <span>{{ $t(link.label) }}</span>
                        </router-link>
                    </nav>
                    <div class="border-top pt-3">
                        <button
                            type="button"
                            class="btn btn-secondary w-100"
                            v-on:click="logout"
                        >
                            <b-icon icon="box-arrow-right" aria-hidden="true" />
                            {{ $t("Kilépés") }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { notifySuccess } from "@/core/services/notify";
import { mapGetters } from "vuex";
import store from "@/core/services/store";
import { VERIFY_AUTH } from "@/core/services/store/auth.module";
import JwtService from "@/core/services/jwt.service";

export default {
    name: "ProfileMenu",
    props: {
        open: { type: Boolean, default: false }
    },
    data() {
        return {
            form: { email: "", password: "" },
            loading: false,
            error: null,
            links: [
                {
                    label: "Profil adatok",
                    icon: "person",
                    to: { name: "Profile", params: { tab: "profile" } }
                },
                {
                    label: "Megrendeléseim",
                    icon: "bag-check",
                    to: { name: "Profile", params: { tab: "orders" } }
                },
                {
                    label: "Matricatervező",
                    icon: "brush",
                    to: { name: "Creator" }
                }
            ]
        };
    },
    computed: {
        ...mapGetters(["currentUser", "isAuthenticated"]),
        url() {
            return `${process.env.VUE_APP_API_URL}`;
        },
        displayName() {
            const user = this.currentUser || {};
            const full = [user.lastname, user.firstname]
                .filter(Boolean)
                .join(" ");
            return full || user.name || user.email || "";
        },
        initials() {
            const user = this.currentUser || {};
            const parts = [user.lastname, user.firstname].filter(Boolean);
            const source = parts.length
                ? parts
                : String(user.name || user.email || "").split(/\s+/);
            return source
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part.charAt(0).toUpperCase())
                .join("");
        }
    },
    watch: {
        open(value) {
            this.error = null;
            if (value && !this.isAuthenticated) {
                this.$nextTick(
                    () => this.$refs.email && this.$refs.email.focus()
                );
            }
        }
    },
    methods: {
        isActive(link) {
            if (link.to.name !== this.$route.name) {
                return false;
            }
            return (
                !link.to.params || link.to.params.tab === this.$route.params.tab
            );
        },
        doLogin() {
            if (!this.form.email || !this.form.password) {
                this.error = this.$translate.t(
                    "Add meg az e-mail címed és a jelszavad."
                );
                return;
            }
            this.loading = true;
            this.error = null;
            this.$http
                .post(this.url + "/login", this.form)
                .then(response => {
                    $cookies.set(
                        "id_token",
                        response.data.token,
                        60 * 60 * 24 * 1000
                    );
                    Promise.all([store.dispatch(VERIFY_AUTH)]).then(() => {
                        JwtService.saveToken(JwtService.getToken());
                    });
                    this.form.password = "";
                    this.$emit("close");
                    notifySuccess(this.$t("Sikeres bejelentkezés."));
                    this.$eventHub.$emit("updateCart", true);
                })
                .catch(() => {
                    this.error = this.$translate.t(
                        "Hibás e-mail cím vagy jelszó."
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        logout() {
            $cookies.remove("id_token");
            this.$emit("close");
            notifySuccess(this.$t("Sikeresen kijelentkeztél."));
            this.$router.replace({ name: "Home" }).catch(() => {});
            Promise.all([store.dispatch(VERIFY_AUTH)]).then(() => {
                JwtService.saveToken(JwtService.getToken());
            });
        }
    }
};
</script>

<style>
.profile-menu {
    position: relative;
}
.profile-menu__toggle {
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    font: inherit;
    text-transform: inherit;
    display: flex;
    align-items: center;
    cursor: pointer;
}
.profile-menu__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--green, #5f9695);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.5px;
    flex-shrink: 0;
}
.profile-menu__avatar--lg {
    width: 48px;
    height: 48px;
    font-size: 17px;
}
.profile-menu__panel {
    text-align: left;
}
.profile-menu__title {
    font-weight: 700;
    margin-bottom: 12px;
}
.profile-menu__user {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--grey, #ced4da);
}
.profile-menu__user-text {
    min-width: 0;
    line-height: 1.3;
}
.profile-menu__links {
    display: flex;
    flex-direction: column;
    padding: 8px 0;
}
.profile-menu__link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 8px;
    border-radius: 8px;
    color: var(--text, #232323);
    text-decoration: none;
}
.profile-menu__link:hover,
.profile-menu__link:focus-visible {
    background: var(--alt-bg, #f6f2ee);
    color: var(--text, #232323);
}
.profile-menu__link.active {
    color: var(--blue, #056e82);
    font-weight: 700;
}
.profile-pop-enter-active,
.profile-pop-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.profile-pop-enter,
.profile-pop-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
